import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu, Search, PieChart, Store, Users, HandHeart, Receipt,
  BadgeDollarSign, QrCode, MapPin, Settings, Plus, Edit3, Download, LogOut, Globe
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  AuthAPI, MerchantsAPI, ReceiversAPI, DonationsAPI, DonorDonationsAPI,
  MenuAPI, SettingsAPI, QRAPI, SearchAPI, CountriesAPI, PaymentsAPI, S3UploadsAPI
} from './api';
import SearchResults from './ui/SearchResults';
import MapView from './ui/mapview';
import PhoneInput from './ui/PhoneInput';
import AddressInput from './ui/AddressInput';
import { COUNTRY_PRESETS, applyCountryPreset } from './countryPresets';

const fmtKD = (n) => `KD ${Number(n ?? 0).toFixed(3)}`;
const cx = (...xs) => xs.filter(Boolean).join(' ');

/* ---------------- Topbar ---------------- */
function Topbar({ onToggleSidebar, onLogout, user, query, onQuery }) {
  return (
    <header className="sticky top-0 z-40 w-full bg-white/90 backdrop-blur border-b border-slate-200">
      <div className="mx-auto max-w-[1440px] px-3 md:px-6 lg:px-8 py-3 flex items-center gap-3">
        <button
          onClick={onToggleSidebar}
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 hover:bg-slate-50"
        >
          <Menu className="h-5 w-5" />
        </button>

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
            placeholder="Search merchants, receivers, donations..."
            value={query}
            onChange={(e) => onQuery(e.target.value)}
          />
        </div>

        <button
          onClick={onLogout}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
        >
          <LogOut className="h-4 w-4" />Logout
        </button>

        <div
          className="h-9 w-9 rounded-full bg-emerald-600 text-white grid place-items-center"
          title={user?.email || 'Admin'}
        >
          RA
        </div>
      </div>
    </header>
  );
}

/* ---------------- Sidebar ---------------- */
const NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: PieChart },
  { key: 'merchants', label: 'Merchants', icon: Store },
  { key: 'receivers', label: 'Receivers', icon: Users },
  { key: 'donations', label: 'Donations', icon: HandHeart },
  { key: 'menu', label: 'Menu & Add-Ons', icon: Receipt },
  { key: 'payments', label: 'Payments', icon: BadgeDollarSign },
  { key: 'qrcodes', label: 'QR Codes', icon: QrCode },
  { key: 'qposter', label: 'QR Poster', icon: QrCode },
  { key: 'map', label: 'Map', icon: MapPin },
  { key: 'countries', label: 'Countries', icon: Globe },
  { key: 'settings', label: 'Settings', icon: Settings },
];

function Sidebar({ current, setCurrent, open }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -20, opacity: 0 }}
          className="fixed md:static z-40 h-full md:h-auto w-[280px] md:w-[260px] bg-white border-r border-slate-200 p-3 md:p-4"
        >
          <div className="mb-3 flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-emerald-600 text-white grid place-items-center">R</div>
            <div className="leading-tight">
              <div className="font-semibold">HarethOptics Admin</div>
              <div className="text-xs text-slate-500">auth enabled</div>
            </div>
          </div>

          <nav className="space-y-1">
            {NAV.map(n => (
              <button
                key={n.key}
                onClick={() => setCurrent(n.key)}
                className={cx(
                  "w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-emerald-50",
                  current === n.key ? "bg-emerald-50 text-emerald-700" : "text-slate-700"
                )}
              >
                <n.icon className="h-4 w-4" />{n.label}
              </button>
            ))}
          </nav>

          <div className="mt-auto pt-4">
            <div className="rounded-lg bg-slate-50 p-3 mb-3">
              <div className="flex items-center gap-2 text-xs">
                <div className="h-2 w-2 rounded-full bg-green-500"></div>
                <span className="text-slate-600">System Status</span>
              </div>
              <div className="text-xs text-green-600 mt-1">All services healthy</div>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

/* ---------------- Generic UI ---------------- */
function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function Table({ cols, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500">
            {cols.map(c => <th key={c.key} className="px-3 py-2 font-medium">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
              {cols.map(c => (
                <td key={c.key} className="px-3 py-2 align-top">
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Modal({ open, onClose, title, children, actions }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className="relative w-full max-w-md sm:max-w-2xl rounded-2xl bg-white shadow-xl border border-slate-200"
          >
            <div className="flex items-center justify-between border-b border-slate-100 p-4">
              <h3 className="text-base font-semibold">{title}</h3>
              <button onClick={onClose} className="rounded-lg p-1 hover:bg-slate-50">✕</button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-y-auto">{children}</div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-3 sm:p-4">{actions}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------------- Dashboard ---------------- */
function Dashboard({ donations, merchants }) {
  const token = localStorage.getItem('token');
  
  // Date range state - default to last 7 days
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  });

  // Commission state
  const [commission, setCommission] = useState(0);

  // Fetch commission percentage
  useEffect(() => {
    MenuAPI.getCommission(token)
      .then(data => setCommission(data.commissionPercent ?? 0))
      .catch(console.error);
  }, [token]);

  // Filter donations based on selected date range
  const filteredDonations = donations.filter(d => {
    if (!d.createdAt) return false;
    const donationDate = new Date(d.createdAt);
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    endDate.setHours(23, 59, 59, 999); // Include the end date fully
    return donationDate >= startDate && donationDate <= endDate;
  });

  // Calculate metrics based on filtered donations
  const totalBase = filteredDonations.reduce((s, d) => s + (d.amount || 0), 0);
  const totalAdd = filteredDonations.reduce((s, d) => s + (d.addonsValue || 0), 0);
  
  // Calculate commission earned using per-merchant rates
  const commissionEarned = filteredDonations.reduce((total, donation) => {
    if (!donation.addonsValue || donation.addonsValue <= 0) return total;
    
    // Find the merchant for this donation
    const merchant = merchants.find(m => m._id === donation.merchant?._id || m._id === donation.merchant);
    const merchantCommission = merchant?.addonCommission !== null ? merchant.addonCommission : commission;
    
    return total + (donation.addonsValue * (merchantCommission / 100));
  }, 0);
  
  const completed = filteredDonations.filter(d => d.status === 'COMPLETED').length;
  const successRate = filteredDonations.length ? Math.round(completed / filteredDonations.length * 100) : 0;

  // Get recent donations (last 5 from filtered)
  const recentDonations = filteredDonations.slice(0, 5);

  // Get top merchants (active ones, sorted by some metric)
  const topMerchants = merchants.filter(m => m.active).slice(0, 3);

  return (
    <div className="space-y-6">
      {/* Date Range Filter Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900">Dashboard Analytics</h3>
            <p className="text-xs text-slate-500 mt-1">
              Showing {filteredDonations.length} donation{filteredDonations.length !== 1 ? 's' : ''} in selected period
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <label className="text-xs text-slate-600 font-medium whitespace-nowrap">From:</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                max={dateRange.endDate}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <label className="text-xs text-slate-600 font-medium whitespace-nowrap">To:</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                min={dateRange.startDate}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Top Row Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-sm text-slate-500">Total Donations</div>
          <div className="mt-1 text-2xl font-semibold">{filteredDonations.length}</div>
          <div className="text-xs text-slate-400 mt-1">In selected period</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-sm text-slate-500">Base Value</div>
          <div className="mt-1 text-2xl font-semibold">{fmtKD(totalBase)}</div>
          <div className="text-xs text-slate-400 mt-1">Menu items</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-sm text-slate-500">Add-Ons Value</div>
          <div className="mt-1 text-2xl font-semibold">{fmtKD(totalAdd)}</div>
          <div className="text-xs text-slate-400 mt-1">Total add-ons</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-sm text-slate-500">Commission Earned</div>
          <div className="mt-1 text-2xl font-semibold">{fmtKD(commissionEarned)}</div>
          <div className="text-xs text-slate-400 mt-1">{commission}% of add-ons</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-sm text-slate-500">Success Rate</div>
          <div className="mt-1 text-2xl font-semibold">{successRate}%</div>
        </div>
      </div>

      {/* Analytics and Recent Donations Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Analytics Section */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center">
              <PieChart className="h-5 w-5 text-slate-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Analytics</h3>
          </div>
          <div className="text-sm text-slate-600 mb-4">Revenue by Merchant (Base + Add-Ons)</div>
          
          {/* Bar Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topMerchants.map((merchant) => {
                  const merchantDonations = filteredDonations.filter(d => d.merchant && d.merchant._id === merchant._id);
                  const merchantRevenue = merchantDonations.reduce((s, d) => s + (d.amount || 0), 0);
                  return {
                    name: merchant.name.length > 12 ? merchant.name.substring(0, 12) + '...' : merchant.name,
                    revenue: merchantRevenue,
                    fullName: merchant.name
                  };
                })}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={{ stroke: '#e2e8f0' }}
                  tickFormatter={(value) => `KD ${value.toFixed(1)}`}
                />
                <Tooltip 
                  formatter={(value, name) => [`KD ${value.toFixed(3)}`, 'Revenue']}
                  labelFormatter={(label, payload) => payload[0]?.payload?.fullName || label}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: '12px'
                  }}
                />
                <Bar 
                  dataKey="revenue" 
                  fill="#10b981" 
                  radius={[4, 4, 0, 0]}
                  maxBarSize={60}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className="text-xs text-slate-400 mt-4">
            Revenue data from merchant donations
          </div>
        </div>

        {/* Recent Donations Section */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center">
              <HandHeart className="h-5 w-5 text-slate-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Recent Donations</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="pb-2">ID</th>
                  <th className="pb-2">Merchant</th>
                  <th className="pb-2">Value</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentDonations.map((donation, index) => (
                  <tr key={donation._id} className="border-b border-slate-50">
                    <td className="py-2 text-slate-600">d{1000 + index}</td>
                    <td className="py-2">
                      {donation.merchant && typeof donation.merchant === 'object' 
                        ? donation.merchant.name 
                        : '—'}
                    </td>
                    <td className="py-2 font-medium">{fmtKD(donation.amount)}</td>
                    <td className="py-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        donation.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                        donation.status === 'ASSIGNED' ? 'bg-blue-100 text-blue-700' :
                        donation.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {donation.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Top Merchants Section */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center">
              <Store className="h-5 w-5 text-slate-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Top Merchants</h3>
          </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="pb-2">Name</th>
                <th className="pb-2">Phone</th>
                <th className="pb-2">Email</th>
                <th className="pb-2">Address</th>
                <th className="pb-2">Menu</th>
                <th className="pb-2">Geofencing</th>
                <th className="pb-2">Active</th>
              </tr>
            </thead>
            <tbody>
              {topMerchants.map((merchant) => (
                <tr key={merchant._id} className="border-b border-slate-50">
                  <td className="py-2 font-medium text-slate-900">{merchant.name}</td>
                  <td className="py-2 text-slate-600">{merchant.phone || '—'}</td>
                  <td className="py-2 text-slate-600">{merchant.email || '—'}</td>
                  <td className="py-2 text-slate-600">{merchant.address || '—'}</td>
                  <td className="py-2 text-slate-600">
                    {merchant.menuItems?.length || 0} items - {merchant.addOns?.length || 0} add-ons
                  </td>
                  <td className="py-2 text-slate-600">
                    Ads: {merchant.geofencingEnabled ? 'On' : 'Off'} - Prefer Receiver: {merchant.preferReceiver ? 'On' : 'Off'}
                  </td>
                  <td className="py-2">
                    <span className={`text-xs ${
                      merchant.active ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {merchant.active ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Merchants ---------------- */
function Switch({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={cx(
        "relative inline-flex h-6 w-11 items-center rounded-full border transition",
        checked ? "bg-emerald-600 border-emerald-700" : "bg-slate-200 border-slate-300"
      )}
    >
      <span
        className={cx(
          "inline-block h-5 w-5 transform rounded-full bg-white shadow transition",
          checked ? "translate-x-5" : "translate-x-1"
        )}
      />
    </button>
  );
}

function Merchants({ token }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', password: '', address: '', lat: '', lng: '', geoAdsEnabled: false, geoPreferReceiverEnabled: false
  });
  const [openEdit, setOpenEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleting, setDeleting] = useState({});
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    email: '',
    password: '', // leave blank to keep existing
    address: '',
    lat: '',
    lng: '',
    active: true,
    geoAdsEnabled: false,
    geoPreferReceiverEnabled: false,
    addonCommission: '',
  });
  const [saving, setSaving] = useState({});

  useEffect(() => { MerchantsAPI.list(token).then(setRows).catch(console.error); }, [token]);

  const add = async () => {
    if (!form.name || !form.phone || !form.email || !form.password || !form.address) return alert('Fill required fields');
    const created = await MerchantsAPI.create(token, {
      ...form,
      lat: Number(form.lat) || 0, lng: Number(form.lng) || 0
    });
    setRows(x => [created, ...x]);
    setOpen(false);
    setForm({ name: '', phone: '', email: '', address: '', password: '', lat: '', lng: '', geoAdsEnabled: false, geoPreferReceiverEnabled: false });
  };

  const toggleField = async (row, field, value) => {
    try {
      setSaving(s => ({ ...s, [row._id]: true }));
      const updated = await MerchantsAPI.patch(token, row._id, { [field]: value });
      setRows(xs => xs.map(x => x._id === row._id ? updated : x));
    } finally { setSaving(s => ({ ...s, [row._id]: false })); }
  };

  const removeRow = async (row) => {
    if (!confirm(`Delete merchant "${row.name}"? This cannot be undone.`)) return;
    try {
      setDeleting(s => ({ ...s, [row._id]: true }));
      await MerchantsAPI.remove(token, row._id);
      setRows(xs => xs.filter(x => x._id !== row._id));
    } catch (e) {
      alert(e.message || "Delete failed");
    } finally {
      setDeleting(s => ({ ...s, [row._id]: false }));
    }
  };

  const openEditFor = (m) => {
    setEditId(m._id);
    setEditForm({
      name: m.name || '',
      phone: m.phone || '',
      email: m.email || '',
      password: '', // optional; blank => do not change
      address: m.address || '',
      lat: m.lat ?? '',
      lng: m.lng ?? '',
      active: m.active ?? true,
      geoAdsEnabled: !!m.geoAdsEnabled,
      geoPreferReceiverEnabled: !!m.geoPreferReceiverEnabled,
      addonCommission: m.addonCommission ?? '',
    });
    setOpenEdit(true);
  };

  const saveEdit = async () => {
    const patch = {
      name: editForm.name?.trim(),
      phone: editForm.phone?.trim(),
      email: editForm.email?.trim(),
      address: editForm.address?.trim(),
      active: !!editForm.active,
      geoAdsEnabled: !!editForm.geoAdsEnabled,
      geoPreferReceiverEnabled: !!editForm.geoPreferReceiverEnabled,
    };
    if (editForm.lat !== '') patch.lat = Number(editForm.lat);
    if (editForm.lng !== '') patch.lng = Number(editForm.lng);
    // Only include password if it's provided and valid
    if (editForm.password && editForm.password.trim() && editForm.password.length >= 3) {
      patch.password = editForm.password;
    }
    if (editForm.addonCommission !== '') {
      patch.addonCommission = editForm.addonCommission === '' ? null : Number(editForm.addonCommission);
    }

    const updated = await MerchantsAPI.patch(token, editId, patch);
    setRows(xs => xs.map(x => x._id === updated._id ? updated : x));
    setOpenEdit(false);
    setEditId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Merchants</h2>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">
          <Plus className="h-4 w-4" />Add Merchant
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <Table
          cols={[
            { key: 'name', label: 'Name' },
            { key: 'phone', label: 'Phone' },
            { key: 'email', label: 'Email' },
            { key: 'address', label: 'Address' },
            {
              key: 'active',
              label: 'Active',
              render: (r) => (
                <div className="flex items-center gap-2 text-xs">
                  <Switch checked={!!r.active} onChange={(v) => toggleField(r, 'active', v)} />
                  {saving[r._id] && <span className="text-[11px] text-emerald-700">Saving…</span>}
                </div>
              ),
            },
            {
              key: 'geo',
              label: 'Geofencing',
              render: (r) => (
                <div className="flex items-center gap-4 text-xs text-slate-600">
                  <div className="flex items-center gap-2">
                    <span>Ads</span>
                    <Switch checked={!!r.geoAdsEnabled} onChange={(v) => toggleField(r, 'geoAdsEnabled', v)} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span>Prefer Receiver</span>
                    <Switch checked={!!r.geoPreferReceiverEnabled} onChange={(v) => toggleField(r, 'geoPreferReceiverEnabled', v)} />
                  </div>
                  {saving[r._id] && <span className="text-[11px] text-emerald-700">Saving…</span>}
                </div>
              ),
            },
            {
              key: 'addonCommission',
              label: 'Add-on Commission',
              render: (r) => (
                <div className="text-xs text-slate-600">
                  {r.addonCommission !== null ? `${r.addonCommission}%` : 'Global'}
                </div>
              ),
            },
            {
              key: 'actions',
              label: 'Actions',
              render: (r) => (
                <button
                  onClick={() => openEditFor(r)}
                  className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-slate-50"
                >
                  <Edit3 className="h-3 w-3" />
                  Edit
                </button>
              ),
            },
            {
              key: 'actions',
              label: 'Actions',
              render: (r) => (
                <div className="flex items-center gap-2">
                  {/* If you already have Edit, put it here */}
                  <button
                    onClick={() => removeRow(r)}
                    disabled={!!deleting[r._id]}
                    className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-red-50 text-red-700 border-red-200 disabled:opacity-50"
                    title="Delete merchant"
                  >
                    {deleting[r._id] ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              )
            }
          ]}
          rows={rows}
        />

      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Add Merchant"
        actions={[
          <button key="c" onClick={() => setOpen(false)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">Cancel</button>,
          <button key="s" onClick={add} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">Save</button>,
        ]}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-600">Business name</label>
            <input 
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors" 
              value={form.name} 
              onChange={e => setForm({ ...form, name: e.target.value })} 
              placeholder="Enter business name"
            />
          </div>
          <PhoneInput
            value={form.phone}
            onChange={(value) => setForm({ ...form, phone: value })}
            label="Phone number"
            required={true}
            className="md:col-span-2"
          />
          <div>
            <label className="text-xs text-slate-600">Email address</label>
            <input 
              type="email" 
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors" 
              value={form.email} 
              onChange={e => setForm({ ...form, email: e.target.value })} 
              placeholder="Enter email address"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Password</label>
            <input 
              type="password" 
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors" 
              value={form.password} 
              onChange={e => setForm({ ...form, password: e.target.value })} 
              placeholder="Enter password"
            />
          </div>
          <AddressInput
            value={form.address}
            onChange={(value) => setForm({ ...form, address: value })}
            label="Complete address"
            required={true}
            className="md:col-span-2"
          />
          <div>
            <label className="text-xs text-slate-600">Latitude</label>
            <input 
              type="number" 
              step="0.0001" 
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors" 
              value={form.lat} 
              onChange={e => setForm({ ...form, lat: e.target.value })} 
              placeholder="e.g., 29.3759"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Longitude</label>
            <input 
              type="number" 
              step="0.0001" 
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors" 
              value={form.lng} 
              onChange={e => setForm({ ...form, lng: e.target.value })} 
              placeholder="e.g., 47.9774"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.geoAdsEnabled} onChange={e => setForm({ ...form, geoAdsEnabled: e.target.checked })} /> Geofencing: Ads
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.geoPreferReceiverEnabled} onChange={e => setForm({ ...form, geoPreferReceiverEnabled: e.target.checked })} /> Geofencing: Prefer Receiver
          </label>
        </div>
      </Modal>
      <Modal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        title="Edit Merchant"
        actions={[
          <button key="c" onClick={() => setOpenEdit(false)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            Cancel
          </button>,
          <button key="s" onClick={saveEdit} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">
            Save
          </button>,
        ]}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-600">Business name</label>
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
          </div>
          <PhoneInput
            value={editForm.phone}
            onChange={(value) => setEditForm({ ...editForm, phone: value })}
            label="Phone number"
            required={true}
            className="md:col-span-2"
          />
          <div>
            <label className="text-xs text-slate-600">Email address</label>
            <input
              type="email"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">New password (optional)</label>
            <input
              type="password"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.password}
              onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
              placeholder="Leave blank to keep existing"
            />
          </div>
          <AddressInput
            value={editForm.address}
            onChange={(value) => setEditForm({ ...editForm, address: value })}
            label="Complete address"
            required={true}
            className="md:col-span-2"
          />
          <div>
            <label className="text-xs text-slate-600">Latitude</label>
            <input
              type="number"
              step="0.0001"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.lat}
              onChange={(e) => setEditForm({ ...editForm, lat: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Longitude</label>
            <input
              type="number"
              step="0.0001"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.lng}
              onChange={(e) => setEditForm({ ...editForm, lng: e.target.value })}
            />
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!editForm.active}
              onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
            />
            Active
          </label>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!editForm.geoAdsEnabled}
              onChange={(e) => setEditForm({ ...editForm, geoAdsEnabled: e.target.checked })}
            />
            Geofencing: Ads
          </label>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!editForm.geoPreferReceiverEnabled}
              onChange={(e) => setEditForm({ ...editForm, geoPreferReceiverEnabled: e.target.checked })}
            />
            Geofencing: Prefer Receiver
          </label>

          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Add-on Commission (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors"
              value={editForm.addonCommission}
              onChange={(e) => setEditForm({ ...editForm, addonCommission: e.target.value })}
              placeholder="Leave empty to use global commission"
            />
            <div className="text-xs text-slate-500 mt-1">
              Leave empty to use global commission setting. Set to 0-100 for merchant-specific commission.
            </div>
          </div>
        </div>
      </Modal>

    </div>
  );
}

/* ---------------- Receivers ---------------- */
function Receivers({ token }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [openEdit, setOpenEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', email: '', active: true, distanceKm: 1, verified: true });
  const [deleting, setDeleting] = useState({});

  useEffect(() => { ReceiversAPI.list(token).then(setRows).catch(console.error); }, [token]);

  const add = async () => {
    if (!form.name || !form.phone) return;
    const c = await ReceiversAPI.create(token, form);
    setRows(x => [c, ...x]);
    setOpen(false);
    setForm({ name: '', phone: '' });
  };

  const openEditFor = (r) => {
    setEditId(r._id);
    setEditForm({
      name: r.name || '',
      phone: r.phone || '',
      email: r.email || '',
      active: r.active ?? true,
      distanceKm: r.distanceKm ?? 1,
      verified: !!r.verified,
    });
    setOpenEdit(true);
  };

  const saveEdit = async () => {
    const patch = {
      name: editForm.name?.trim(),
      phone: editForm.phone?.trim(),
      email: editForm.email?.trim() || undefined,
      active: !!editForm.active,
      distanceKm: Number(editForm.distanceKm) || 1,
      verified: !!editForm.verified,
    };
    const updated = await ReceiversAPI.patch(token, editId, patch);
    setRows(xs => xs.map(x => x._id === updated._id ? updated : x));
    setOpenEdit(false);
    setEditId(null);
  };

  const toggleField = async (row, field, value) => {
    const updated = await ReceiversAPI.patch(token, row._id, { [field]: value });
    setRows(xs => xs.map(x => x._id === row._id ? updated : x));
  };

  const removeRow = async (row) => {
    if (!confirm(`Delete receiver "${row.name}"? This cannot be undone.`)) return;
    try {
      setDeleting(s => ({ ...s, [row._id]: true }));
      await ReceiversAPI.remove(token, row._id);
      setRows(xs => xs.filter(x => x._id !== row._id));
    } catch (e) {
      alert(e.message || 'Delete failed');
    } finally {
      setDeleting(s => ({ ...s, [row._id]: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Receivers</h2>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">
          <Plus className="h-4 w-4" />Add
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <Table
          cols={[
            { key: 'name', label: 'Name' },
            { key: 'phone', label: 'Phone' },
            { key: 'distanceKm', label: 'Distance', render: r => (r.distanceKm ?? '—') + ' km' },
            { key: 'verified', label: 'Verified', render: r => r.verified ? 'Yes' : 'No' },
            { key: 'active', label: 'Active', render: r => (
              <div className="flex items-center gap-2 text-xs">
                <Switch checked={!!r.active} onChange={(v) => toggleField(r, 'active', v)} />
              </div>
            ) },
            { key: 'actions', label: 'Actions', render: r => (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditFor(r)}
                  className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-slate-50"
                >
                  <Edit3 className="h-3 w-3" />Edit
                </button>
                <button
                  onClick={() => removeRow(r)}
                  disabled={!!deleting[r._id]}
                  className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-red-50 text-red-700 border-red-200 disabled:opacity-50"
                  title="Delete receiver"
                >
                  {deleting[r._id] ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            ) }
          ]}
          rows={rows}
        />
      </div>

      <Modal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        title="Edit Receiver"
        actions={[
          <button key="c" onClick={() => setOpenEdit(false)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">Cancel</button>,
          <button key="s" onClick={saveEdit} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">Save</button>
        ]}
      >
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-600">Full name</label>
            <input className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
          </div>
          <PhoneInput
            value={editForm.phone}
            onChange={(value) => setEditForm({ ...editForm, phone: value })}
            label="Phone"
            required={true}
          />
          <div>
            <label className="text-xs text-slate-600">Email</label>
            <input type="email" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-slate-600">Distance (km)</label>
            <input type="number" step="0.1" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" value={editForm.distanceKm} onChange={e => setEditForm({ ...editForm, distanceKm: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!editForm.verified} onChange={e => setEditForm({ ...editForm, verified: e.target.checked })} /> Verified
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!editForm.active} onChange={e => setEditForm({ ...editForm, active: e.target.checked })} /> Active
            </label>
          </div>
        </div>
      </Modal>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Add Receiver"
        actions={[
          <button key="c" onClick={() => setOpen(false)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">Cancel</button>,
          <button key="s" onClick={add} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">Save</button>
        ]}
      >
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-600">Full name</label>
            <input 
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors" 
              value={form.name} 
              onChange={e => setForm({ ...form, name: e.target.value })} 
              placeholder="Enter full name"
            />
          </div>
          <PhoneInput
            value={form.phone}
            onChange={(value) => setForm({ ...form, phone: value })}
            label="Phone"
            required={true}
          />
        </div>
      </Modal>
    </div>
  );
}

/* ---------------- Donations ---------------- */
function Donations({ token }) {
  const [rows, setRows] = useState([]);
  const [viewDonation, setViewDonation] = useState(null);
  
  useEffect(() => {
    // Fetch donations immediately
    const fetchDonations = () => {
      DonorDonationsAPI.list(token).then(setRows).catch(console.error);
    };
    
    fetchDonations();
    
    // Set up polling every 5 seconds to check for new donations
    const pollInterval = setInterval(() => {
      fetchDonations();
    }, 3000); // Poll every 5 seconds
    
    // Cleanup interval on unmount
    return () => clearInterval(pollInterval);
  }, [token]);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Donor Donations</h2>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <Table
          cols={[
            { key: '_id', label: 'ID' },
            { key: 'donor', label: 'Donor', render: r => `${r.donorName}${r.donorPhone ? ` • ${r.donorPhone}` : ''}` },
            { key: 'amount', label: 'Amount', render: r => `${(r.currency || 'kwd').toUpperCase()} ${Number(r.amount || 0).toFixed(3)}` },
            { key: 'merchant', label: 'Merchant', render: r => (typeof r.merchant === 'object' && r.merchant?.name) ? r.merchant.name : '—' },
            { key: 'receiver', label: 'Receiver', render: r => (typeof r.receiver === 'object' && r.receiver?.name) ? r.receiver.name : (r.receiver || '—') },
            { key: 'paymentMethod', label: 'Payment' },
            { key: 'status', label: 'Status' },
            { key: 'createdAt', label: 'Created', render: r => r.createdAt ? new Date(r.createdAt).toLocaleString() : '—' },
            { key: 'actions', label: '', render: r => (
              <button onClick={() => setViewDonation(r)} className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-slate-50">
                View details
              </button>
            ) }
          ]}
          rows={rows}
        />
      </div>

      {viewDonation && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setViewDonation(null)} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Donation Details</h3>
              <button onClick={() => setViewDonation(null)} className="rounded-lg p-1 hover:bg-slate-50">✕</button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <div className="text-xs text-slate-600 mb-1">Donation ID</div>
                  <div className="font-semibold truncate">{viewDonation._id}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-600 mb-1">Created</div>
                  <div className="font-semibold">{viewDonation.createdAt ? new Date(viewDonation.createdAt).toLocaleString() : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-600 mb-1">Status</div>
                  <div className="font-semibold capitalize">{(viewDonation.status || 'pending').toLowerCase()}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-600 mb-1">Payment</div>
                  <div className="font-semibold capitalize">{viewDonation.paymentMethod || '—'}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Donor</h4>
                  <div className="text-sm">{viewDonation.donorName || 'Anonymous Donor'}</div>
                  {viewDonation.donorPhone && <div className="text-xs text-slate-600 mt-1">{viewDonation.donorPhone}</div>}
                  {viewDonation.donorEmail && <div className="text-xs text-slate-600 mt-1">{viewDonation.donorEmail}</div>}
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Merchant</h4>
                  <div className="text-sm">{typeof viewDonation.merchant === 'object' ? (viewDonation.merchant?.name || '—') : (viewDonation.merchant || '—')}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Receiver</h4>
                  <div className="text-sm">{typeof viewDonation.receiver === 'object' ? (viewDonation.receiver?.name || '—') : (viewDonation.receiver || '—')}</div>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold mb-2">Amounts</h4>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div><span className="text-slate-600">Base:</span> {(Number(viewDonation.amount || 0)).toFixed(3)} {(viewDonation.currency || 'KWD').toUpperCase()}</div>
                  {Array.isArray(viewDonation.addons) && viewDonation.addons.length > 0 && (
                    <div><span className="text-slate-600">Add-ons:</span> {viewDonation.addons.map(a => `${a.name} (${Number(a.price||0).toFixed(3)})`).join(', ')}</div>
                  )}
                  {typeof viewDonation.totalAmount === 'number' && (
                    <div className="font-semibold text-emerald-700">Total: {Number(viewDonation.totalAmount).toFixed(3)} {(viewDonation.currency || 'KWD').toUpperCase()}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200">
              <button onClick={() => setViewDonation(null)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm hover:bg-slate-50">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Menu & Add-Ons ---------------- */
function MenuView({ token }) {
  const [commission, setCommission] = useState(10);
  const [items, setItems] = useState([]);
  const [addons, setAddons] = useState([]);
  const [deleting, setDeleting] = useState({});
  const [editType, setEditType] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeEditLang, setActiveEditLang] = useState('en');

  useEffect(() => {
    (async () => {
      const [it, ad, c] = await Promise.all([
        MenuAPI.items(token), MenuAPI.addons(token), MenuAPI.getCommission(token)
      ]);
      setItems(it); setAddons(ad); setCommission(c.commissionPercent ?? 10);
    })().catch(console.error);
  }, [token]);

  const openEdit = (type, row) => { 
    setEditType(type);
    
    if (type === 'item') {
      // Initialize imageUrls from either imageUrls array, single imageUrl, or empty array
      let imageUrls = [];
      if (row.imageUrls && row.imageUrls.length > 0) {
        imageUrls = [...row.imageUrls];
      } else if (row.imageUrl) {
        imageUrls = [row.imageUrl];
      } else {
        imageUrls = []; // Start with empty array, no default empty string
      }
      
      setEditRow({ 
        ...row, 
        imageUrls,
        description: row.description || '',
        // Initialize multilingual fields
        nameTranslations: row.nameTranslations || { en: row.name || '', ar: row.name || '' },
        descriptionTranslations: row.descriptionTranslations || { en: row.description || '', ar: row.description || '' },
        overlayText: {
          mainText: row.overlayText?.mainText || "Make your meal matter",
          subText: row.overlayText?.subText || "Small fee, big impact"
        },
        // Initialize multilingual overlay text
        overlayTextTranslations: row.overlayTextTranslations || {
          en: {
            mainText: row.overlayText?.mainText || "Make your meal matter",
            subText: row.overlayText?.subText || "Small fee, big impact"
          },
          ar: {
            mainText: row.overlayTextTranslations?.ar?.mainText || "اجعل وجبتك مهمة",
            subText: row.overlayTextTranslations?.ar?.subText || "رسوم صغيرة، تأثير كبير"
          }
        },
        overlayAmounts: {
          enabled: row.overlayAmounts?.enabled || false,
          amounts: row.overlayAmounts?.amounts || [0.250, 0.500],
          currency: row.overlayAmounts?.currency || "KWD",
          allowCustom: row.overlayAmounts?.allowCustom !== false
        }
      });
    } else {
      // For add-ons, use single image field
      setEditRow({ 
        ...row, 
        image: row.image || '',
        // Initialize multilingual fields for add-ons
        nameTranslations: row.nameTranslations || { en: row.name || '', ar: row.name || '' }
      });
    }
  };
  const closeEdit = () => { setEditType(null); setEditRow(null); };
  const saveEdit = async () => {
    if (!editRow?.nameTranslations?.en || !editRow?.nameTranslations?.ar || isNaN(Number(editRow?.price))) {
      return alert('Name in both languages and numeric price required');
    }
    
    if (editType === 'item') {
      const imageUrls = Array.isArray(editRow.imageUrls) 
        ? editRow.imageUrls.filter(url => url && url.trim()) 
        : [];
      const u = await MenuAPI.patchItem(token, editRow._id, { 
        name: editRow.nameTranslations.en, // Keep backward compatibility
        price: Number(editRow.price), 
        imageUrls,
        description: editRow.descriptionTranslations?.en?.trim() || undefined, // Keep backward compatibility
        nameTranslations: editRow.nameTranslations,
        descriptionTranslations: editRow.descriptionTranslations,
        overlayText: editRow.overlayText, // Keep for backward compatibility
        overlayTextTranslations: editRow.overlayTextTranslations,
        overlayAmounts: editRow.overlayAmounts
      });
      setItems(xs => xs.map(x => x._id === u._id ? u : x));
    }
    if (editType === 'addon') {
      const u = await MenuAPI.patchAddon(token, editRow._id, { 
        name: editRow.nameTranslations.en, // Keep backward compatibility
        price: Number(editRow.price), 
        image: editRow.image?.trim() || "",
        nameTranslations: editRow.nameTranslations
      });
      setAddons(xs => xs.map(x => x._id === u._id ? u : x));
    }
    closeEdit();
  };
  const addItem = async () => { 
    const c = await MenuAPI.addItem(token, { 
      name: 'New Item', 
      price: 0.000, 
      imageUrls: [], 
      description: '',
      nameTranslations: { en: 'New Item', ar: 'عنصر جديد' },
      descriptionTranslations: { en: '', ar: '' },
      overlayTextTranslations: {
        en: { mainText: 'Make your meal matter', subText: 'Small fee, big impact' },
        ar: { mainText: 'اجعل وجبتك مهمة', subText: 'رسوم صغيرة، تأثير كبير' }
      }
    }); 
    setItems(xs => [c, ...xs]); 
  };
  const addAddon = async () => { 
    const c = await MenuAPI.addAddon(token, { 
      name: 'New Add-On', 
      price: 0.000, 
      image: '',
      nameTranslations: { en: 'New Add-On', ar: 'إضافة جديدة' }
    }); 
    setAddons(xs => [c, ...xs]); 
  };
  const saveCommission = async () => {
    const r = await MenuAPI.setCommission(token, Number(commission) || 0);
    setCommission(r.commissionPercent); alert('Commission saved');
  };

  // File upload functions
  const handleFileUpload = async (files, isMultiple = false) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      const fileArray = Array.from(files);
      const result = isMultiple 
        ? await S3UploadsAPI.uploadImages(token, fileArray)
        : await S3UploadsAPI.uploadImage(token, fileArray[0]);
      
      if (isMultiple && result.files) {
        const newUrls = result.files.map(file => file.url);
        setEditRow(prev => ({
          ...prev,
          imageUrls: [...(prev.imageUrls || []), ...newUrls]
        }));
      } else if (result.url) {
        setEditRow(prev => ({
          ...prev,
          imageUrls: [...(prev.imageUrls || []), result.url]
        }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image(s). Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files, true);
    }
  };

  const removeImageUrl = (index) => {
    setEditRow(prev => ({
      ...prev,
      imageUrls: (prev.imageUrls || []).filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Products/Items</h3>
          <button onClick={addItem} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">Add Item</button>
        </div>
        <div className="mt-3">
          <Table
            cols={[
              { key: 'imageUrl', label: '', render: r => {
                const firstImage = r.imageUrls?.[0] || r.imageUrl;
                const imageCount = r.imageUrls?.length || 0;
                return firstImage ? (
                  <div className="relative inline-block">
                    <img src={firstImage} alt={r.name} className="h-8 w-8 rounded object-cover" />
                    {imageCount > 1 && (
                      <span className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 bg-green-600 text-white text-[9px] font-semibold rounded-full w-4 h-4 flex items-center justify-center shadow-md border border-white">
                        {imageCount}
                      </span>
                    )}
                  </div>
                ) : (<div className="h-8 w-8 rounded bg-slate-100 grid place-items-center text-[10px] text-slate-400">No</div>);
              }},
              { key: 'name', label: 'Name' },
              { key: 'price', label: 'Price', render: r => fmtKD(r.price) },
              {
                key: 'edit', label: '', render: r => (
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit('item', r)} className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-slate-50">
                      <Edit3 className="h-3 w-3" />Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm(`Delete item "${r.name}"? This cannot be undone.`)) return;
                        try {
                          setDeleting(s => ({ ...s, [r._id]: true }));
                          await MenuAPI.removeItem(token, r._id);
                          setItems(xs => xs.filter(x => x._id !== r._id));
                        } catch (e) {
                          alert(e.message || 'Delete failed');
                        } finally {
                          setDeleting(s => ({ ...s, [r._id]: false }));
                        }
                      }}
                      disabled={!!deleting[r._id]}
                      className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-red-50 text-red-700 border-red-200 disabled:opacity-50"
                      title="Delete item"
                    >
                      {deleting[r._id] ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                )
              }
            ]}
            rows={items}
          />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="text-sm font-semibold">Add-Ons & Commission</div>
        <div className="mt-2 flex items-center gap-2">
          <input
            type="number" min={0} max={100}
            value={commission}
            onChange={e => setCommission(e.target.value)}
            className="w-20 rounded-xl border border-slate-200 px-3 py-2"
          />
          <span className="text-sm text-slate-600">%</span>
          <button onClick={saveCommission} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">Save</button>
        </div>

        <div className="mt-3">
          <Table
            cols={[
              { key: 'imageUrl', label: '', render: r => {
                const singleImage = r.image;
                return singleImage ? (
                  <img src={singleImage} alt={r.name} className="h-8 w-8 rounded object-cover" />
                ) : (<div className="h-8 w-8 rounded bg-slate-100 grid place-items-center text-[10px] text-slate-400">No</div>);
              }},
              { key: 'name', label: 'Name' },
              { key: 'price', label: 'Price', render: r => fmtKD(r.price) },
              {
                key: 'edit', label: '', render: r => (
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit('addon', r)} className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-slate-50">
                      <Edit3 className="h-3 w-3" />Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm(`Delete add-on "${r.name}"? This cannot be undone.`)) return;
                        try {
                          setDeleting(s => ({ ...s, [r._id]: true }));
                          await MenuAPI.removeAddon(token, r._id);
                          setAddons(xs => xs.filter(x => x._id !== r._id));
                        } catch (e) {
                          alert(e.message || 'Delete failed');
                        } finally {
                          setDeleting(s => ({ ...s, [r._id]: false }));
                        }
                      }}
                      disabled={!!deleting[r._id]}
                      className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-red-50 text-red-700 border-red-200 disabled:opacity-50"
                      title="Delete add-on"
                    >
                      {deleting[r._id] ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                )
              }
            ]}
            rows={addons}
          />
        </div>

        <div className="mt-3">
          <button onClick={addAddon} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">Add Add-On</button>
        </div>
      </div>

      <Modal
        open={!!editType}
        onClose={closeEdit}
        title={activeEditLang === 'en' ? `Edit ${editType === 'item' ? 'Item' : 'Add-On'}` : `تعديل ${editType === 'item' ? 'العنصر' : 'الإضافة'}`}
        actions={[
          <button key="c" onClick={closeEdit} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">{activeEditLang === 'en' ? 'Cancel' : 'إلغاء'}</button>,
          <button key="s" onClick={saveEdit} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">{activeEditLang === 'en' ? 'Save' : 'حفظ'}</button>
        ]}
      >
        {/* Language Tabs */}
        <div className="flex space-x-1 mb-6 bg-slate-100 p-1 rounded-xl">
          <button
            onClick={() => setActiveEditLang('en')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${
              activeEditLang === 'en' 
                ? 'bg-white text-slate-900 shadow-sm' 
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            English
          </button>
          <button
            onClick={() => setActiveEditLang('ar')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${
              activeEditLang === 'ar' 
                ? 'bg-white text-slate-900 shadow-sm' 
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            العربية
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={`text-xs text-slate-600 ${activeEditLang === 'ar' ? 'text-right' : ''}`}>
              {activeEditLang === 'en' ? 'Name (English)' : 'الاسم (العربية)'}
            </label>
            <input 
              className={`mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors ${
                activeEditLang === 'ar' ? 'text-right' : ''
              }`}
              value={editRow?.nameTranslations?.[activeEditLang] || ''} 
              onChange={e => setEditRow({ 
                ...editRow, 
                nameTranslations: { 
                  ...editRow.nameTranslations, 
                  [activeEditLang]: e.target.value 
                } 
              })} 
              dir={activeEditLang === 'ar' ? 'rtl' : 'ltr'}
              placeholder={activeEditLang === 'en' ? 'Item name in English' : 'اسم العنصر بالعربية'}
            />
          </div>
          <div>
            <label className={`text-xs text-slate-600 ${activeEditLang === 'ar' ? 'text-right' : ''}`}>
              {activeEditLang === 'en' ? 'Price' : 'السعر'}
            </label>
            <input type="number" step="0.001" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" value={editRow?.price ?? ''} onChange={e => setEditRow({ ...editRow, price: e.target.value })} />
          </div>
          {editType === 'item' && (
            <div className="md:col-span-2">
              <label className={`text-xs text-slate-600 ${activeEditLang === 'ar' ? 'text-right' : ''}`}>
                {activeEditLang === 'en' ? 'Description (English)' : 'الوصف (العربية)'}
              </label>
              <textarea 
                className={`mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors ${
                  activeEditLang === 'ar' ? 'text-right' : ''
                }`}
                rows={3} 
                value={editRow?.descriptionTranslations?.[activeEditLang] || ''} 
                onChange={e => setEditRow({ 
                  ...editRow, 
                  descriptionTranslations: { 
                    ...editRow.descriptionTranslations, 
                    [activeEditLang]: e.target.value 
                  } 
                })} 
                placeholder={activeEditLang === 'en' ? 'Item description in English...' : 'وصف العنصر بالعربية...'}
                dir={activeEditLang === 'ar' ? 'rtl' : 'ltr'}
              />
            </div>
          )}
          
          {/* Image Overlay Configuration - Only for items */}
          {editType === 'item' && (
            <>
            <div className="md:col-span-2">
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className={`text-sm font-medium text-slate-700 mb-3 ${activeEditLang === 'ar' ? 'text-right' : ''}`}>
                    {activeEditLang === 'en' ? 'Image Overlay Configuration' : 'إعدادات تراكب الصورة'}
                  </h4>
                  
                  {/* Overlay Text */}
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className={`text-xs text-slate-600 ${activeEditLang === 'ar' ? 'text-right' : ''}`}>
                        {activeEditLang === 'en' ? 'Main Text (e.g., "Make your meal matter")' : 'النص الرئيسي (مثال: "اجعل وجبتك مهمة")'}
                      </label>
                      <input 
                        className={`mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors ${
                          activeEditLang === 'ar' ? 'text-right' : ''
                        }`}
                        value={editRow?.overlayTextTranslations?.[activeEditLang]?.mainText || ''} 
                        onChange={e => setEditRow({ 
                          ...editRow, 
                          overlayTextTranslations: { 
                            ...editRow.overlayTextTranslations, 
                            [activeEditLang]: {
                              ...editRow.overlayTextTranslations?.[activeEditLang],
                              mainText: e.target.value 
                            }
                          } 
                        })} 
                        placeholder={activeEditLang === 'en' ? 'Make your meal matter' : 'اجعل وجبتك مهمة'}
                        dir={activeEditLang === 'ar' ? 'rtl' : 'ltr'}
                      />
                    </div>
                    <div>
                      <label className={`text-xs text-slate-600 ${activeEditLang === 'ar' ? 'text-right' : ''}`}>
                        {activeEditLang === 'en' ? 'Sub Text (e.g., "Small fee, big impact")' : 'النص الفرعي (مثال: "رسوم صغيرة، تأثير كبير")'}
                      </label>
                      <input 
                        className={`mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors ${
                          activeEditLang === 'ar' ? 'text-right' : ''
                        }`}
                        value={editRow?.overlayTextTranslations?.[activeEditLang]?.subText || ''} 
                        onChange={e => setEditRow({ 
                          ...editRow, 
                          overlayTextTranslations: { 
                            ...editRow.overlayTextTranslations, 
                            [activeEditLang]: {
                              ...editRow.overlayTextTranslations?.[activeEditLang],
                              subText: e.target.value 
                            }
                          } 
                        })} 
                        placeholder={activeEditLang === 'en' ? 'Small fee, big impact' : 'رسوم صغيرة، تأثير كبير'}
                        dir={activeEditLang === 'ar' ? 'rtl' : 'ltr'}
                      />
                    </div>
                  </div>

                  {/* Overlay Amounts */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        id="overlayEnabled"
                        checked={editRow?.overlayAmounts?.enabled || false}
                        onChange={e => setEditRow({ 
                          ...editRow, 
                          overlayAmounts: { 
                            ...editRow.overlayAmounts, 
                            enabled: e.target.checked 
                          } 
                        })}
                        className="rounded border-slate-300"
                      />
                      <label htmlFor="overlayEnabled" className={`text-xs text-slate-600 ${activeEditLang === 'ar' ? 'text-right' : ''}`}>
                        {activeEditLang === 'en' ? 'Enable amount buttons on image' : 'تفعيل أزرار المبالغ على الصورة'}
                      </label>
                    </div>

                    {editRow?.overlayAmounts?.enabled && (
                      <div className="space-y-3 pl-4 border-l-2 border-slate-200">
                        <div>
                          <label className={`text-xs text-slate-600 ${activeEditLang === 'ar' ? 'text-right' : ''}`}>
                            {activeEditLang === 'en' ? 'Amount Buttons (one per line, in KWD)' : 'أزرار المبالغ (واحد في كل سطر، بالدينار الكويتي)'}
                          </label>
              <div className="space-y-2 mt-1">
                            {(editRow?.overlayAmounts?.amounts || []).map((amount, idx) => (
                              <div key={idx} className="flex gap-2">
                                <input 
                                  type="number" 
                                  step="0.001"
                                  className="flex-1 rounded-xl border border-slate-200 px-3 py-2" 
                                  value={amount} 
                                  onChange={e => {
                                    const newAmounts = [...(editRow.overlayAmounts.amounts || [])];
                                    newAmounts[idx] = Number(e.target.value) || 0;
                                    setEditRow({ 
                                      ...editRow, 
                                      overlayAmounts: { 
                                        ...editRow.overlayAmounts, 
                                        amounts: newAmounts 
                                      } 
                                    });
                                  }} 
                                  placeholder="0.250"
                                />
                                <button 
                                  onClick={() => {
                                    const newAmounts = (editRow.overlayAmounts.amounts || []).filter((_, i) => i !== idx);
                                    setEditRow({ 
                                      ...editRow, 
                                      overlayAmounts: { 
                                        ...editRow.overlayAmounts, 
                                        amounts: newAmounts 
                                      } 
                                    });
                                  }}
                                  className="rounded-xl border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                                >
                                  {activeEditLang === 'en' ? 'Remove' : 'إزالة'}
                                </button>
                              </div>
                            ))}
                            <button 
                              onClick={() => {
                                const newAmounts = [...(editRow?.overlayAmounts?.amounts || []), 0.000];
                                setEditRow({ 
                                  ...editRow, 
                                  overlayAmounts: { 
                                    ...editRow.overlayAmounts, 
                                    amounts: newAmounts 
                                  } 
                                });
                              }}
                              className="rounded-xl border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                            >
                              {activeEditLang === 'en' ? '+ Add Amount' : '+ إضافة مبلغ'}
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={`text-xs text-slate-600 ${activeEditLang === 'ar' ? 'text-right' : ''}`}>
                              {activeEditLang === 'en' ? 'Currency' : 'العملة'}
                            </label>
                            <select 
                              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" 
                              value={editRow?.overlayAmounts?.currency || 'KWD'} 
                              onChange={e => setEditRow({ 
                                ...editRow, 
                                overlayAmounts: { 
                                  ...editRow.overlayAmounts, 
                                  currency: e.target.value 
                                } 
                              })}
                            >
                              <option value="KWD">KWD</option>
                            </select>
                          </div>
                          <div className="flex items-center gap-2 pt-6">
                            <input 
                              type="checkbox" 
                              id="allowCustom"
                              checked={editRow?.overlayAmounts?.allowCustom !== false}
                              onChange={e => setEditRow({ 
                                ...editRow, 
                                overlayAmounts: { 
                                  ...editRow.overlayAmounts, 
                                  allowCustom: e.target.checked 
                                } 
                              })}
                              className="rounded border-slate-300"
                            />
                            <label htmlFor="allowCustom" className={`text-xs text-slate-600 ${activeEditLang === 'ar' ? 'text-right' : ''}`}>
                              {activeEditLang === 'en' ? 'Allow custom amount' : 'السماح بمبلغ مخصص'}
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Multiple images for items */}
          {editType === 'item' && (
            <div className="md:col-span-2">
              <label className="text-xs text-slate-600">Images</label>
              
              {/* File Upload Section */}
              <div className="mt-1 mb-3 p-4 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50">
                <div className="text-center">
                  <input
                    type="file"
                    id="image-upload"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="image-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                      uploading 
                        ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Upload Images
                      </>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Select multiple images (JPEG, PNG, GIF, WEBP) - Max 20MB each
                  </p>
                </div>
              </div>

              {/* URL Input Section */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-px bg-slate-200 flex-1"></div>
                  <span className="text-xs text-slate-500">OR</span>
                  <div className="h-px bg-slate-200 flex-1"></div>
                </div>
                
                {(editRow?.imageUrls || []).length > 0 ? (
                  (editRow?.imageUrls || []).map((url, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input 
                      className="flex-1 rounded-xl border border-slate-200 px-3 py-2" 
                      value={url} 
                      onChange={e => {
                        const newUrls = [...(editRow.imageUrls || [])];
                        newUrls[idx] = e.target.value;
                        setEditRow({ ...editRow, imageUrls: newUrls });
                      }} 
                      placeholder="https://..." 
                    />
                    <button 
                        onClick={() => removeImageUrl(idx)}
                      className="rounded-xl border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                    >
                      {activeEditLang === 'en' ? 'Remove' : 'إزالة'}
                    </button>
                  </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500 py-2">
                    No images added yet. Upload files or add URLs above.
                  </div>
                )}
                <button 
                  onClick={() => {
                    const newUrls = [...(editRow?.imageUrls || []), ''];
                    setEditRow({ ...editRow, imageUrls: newUrls });
                  }}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                >
                  {activeEditLang === 'en' ? '+ Add Image URL' : '+ إضافة رابط الصورة'}
                </button>
              </div>
              
              {/* Image Previews */}
              {editRow?.imageUrls?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {editRow.imageUrls.filter(url => url && url.trim()).map((url, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={url} 
                        alt={`Preview ${idx + 1}`} 
                        className="h-20 w-20 rounded object-cover border" 
                        onError={(e) => { e.target.style.display = 'none'; }} 
                      />
                      <button
                        onClick={() => removeImageUrl(idx)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Single image for add-ons */}
          {editType === 'addon' && (
            <div className="md:col-span-2">
              <label className="text-xs text-slate-600">Image</label>
              
              {/* File Upload Section */}
              <div className="mt-1 mb-3 p-4 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50">
                <div className="text-center">
              <input 
                    type="file"
                    id="addon-image-upload"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      setUploading(true);
                      try {
                        const response = await S3UploadsAPI.uploadImage(token, file);
                        setEditRow({ ...editRow, image: response.url });
                      } catch (error) {
                        console.error('Upload failed:', error);
                        alert('Upload failed. Please try again.');
                      } finally {
                        setUploading(false);
                        e.target.value = ''; // Reset file input
                      }
                    }}
                    className="hidden"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="addon-image-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                      uploading 
                        ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Upload Image
                      </>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Select an image (JPEG, PNG, GIF, WEBP) - Max 10MB
                  </p>
                </div>
              </div>

              {/* URL Input Section */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-px bg-slate-200 flex-1"></div>
                  <span className="text-xs text-slate-500">OR</span>
                  <div className="h-px bg-slate-200 flex-1"></div>
                </div>
                
                <input 
                  className="w-full rounded-xl border border-slate-200 px-3 py-2" 
                value={editRow?.image || ''} 
                onChange={e => setEditRow({ ...editRow, image: e.target.value })} 
                placeholder="https://..." 
              />
              </div>
              
              {/* Image Preview */}
              {editRow?.image && (
                <div className="mt-3 flex items-center gap-3">
                  <img 
                    src={editRow.image} 
                    alt="Preview" 
                    className="h-24 w-24 rounded object-cover border" 
                    onError={(e) => { e.target.style.display = 'none'; }} 
                  />
                  <button
                    onClick={() => setEditRow({ ...editRow, image: '' })}
                    className="p-2 text-red-700 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                    title={activeEditLang === 'en' ? 'Remove Image' : 'إزالة الصورة'}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

/* ---------------- QR Codes ---------------- */
function QRCodes() {
  const code = `https://admin.repleeno.com`;
  const qrUrl = QRAPI.url(code, 300);

  const downloadPng = () => {
    const a = document.createElement('a');
    a.href = qrUrl; a.download = `${code}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold">Admin QR Code</h3>
          </div>
          <button onClick={downloadPng} className="rounded-xl border border-slate-200 px-3 py-2 text-sm inline-flex items-center gap-2 hover:bg-slate-50">
            <Download className="h-4 w-4" />Download PNG
          </button>
        </div>

        <div className="rounded-xl border grid place-items-center p-2 bg-white">
          <img src={qrUrl} alt="Admin QR" className="w-[300px] h-[300px]" />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        QR generated by backend <code>/api/qr</code>.
      </div>
    </div>
  );
}

/* ---------------- Countries ---------------- */
function Countries({ token }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', code: '', phoneCode: '', phoneLength: '', currency: '', currencySymbol: '', language: 'en'
  });
  const [openEdit, setOpenEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleting, setDeleting] = useState({});
  const [editForm, setEditForm] = useState({
    name: '',
    code: '',
    phoneCode: '',
    phoneLength: '',
    currency: '',
    currencySymbol: '',
    language: 'en',
    active: true,
  });
  const [saving, setSaving] = useState({});

  useEffect(() => { CountriesAPI.list(token).then(setRows).catch(console.error); }, [token]);

  const add = async () => {
    if (!form.name || !form.code || !form.phoneCode || !form.phoneLength) return alert('Fill required fields');
    const created = await CountriesAPI.create(token, {
      ...form,
      phoneLength: Number(form.phoneLength) || 0
    });
    setRows(x => [created, ...x]);
    setOpen(false);
    setForm({ name: '', code: '', phoneCode: '', phoneLength: '', currency: '', currencySymbol: '', language: 'en' });
  };

  const toggleField = async (row, field, value) => {
    try {
      setSaving(s => ({ ...s, [row._id]: true }));
      const updated = await CountriesAPI.patch(token, row._id, { [field]: value });
      setRows(xs => xs.map(x => x._id === row._id ? updated : x));
    } finally { setSaving(s => ({ ...s, [row._id]: false })); }
  };

  const removeRow = async (row) => {
    if (!confirm(`Delete country "${row.name}"? This cannot be undone.`)) return;
    try {
      setDeleting(s => ({ ...s, [row._id]: true }));
      await CountriesAPI.remove(token, row._id);
      setRows(xs => xs.filter(x => x._id !== row._id));
    } catch (e) {
      alert(e.message || "Delete failed");
    } finally {
      setDeleting(s => ({ ...s, [row._id]: false }));
    }
  };

  const openEditFor = (c) => {
    setEditId(c._id);
    setEditForm({
      name: c.name || '',
      code: c.code || '',
      phoneCode: c.phoneCode || '',
      phoneLength: c.phoneLength ?? '',
      currency: c.currency || '',
      currencySymbol: c.currencySymbol || '',
      language: c.language || 'en',
      active: c.active ?? true,
    });
    setOpenEdit(true);
  };

  const saveEdit = async () => {
    const patch = {
      name: editForm.name?.trim(),
      code: editForm.code?.trim(),
      phoneCode: editForm.phoneCode?.trim(),
      phoneLength: Number(editForm.phoneLength) || 0,
      currency: editForm.currency?.trim(),
      currencySymbol: editForm.currencySymbol?.trim(),
      language: editForm.language === 'ar' ? 'ar' : 'en',
      active: !!editForm.active,
    };

    const updated = await CountriesAPI.patch(token, editId, patch);
    setRows(xs => xs.map(x => x._id === updated._id ? updated : x));
    setOpenEdit(false);
    setEditId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Countries</h2>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">
          <Plus className="h-4 w-4" />Add Country
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <Table
          cols={[
            { key: 'name', label: 'Country Name' },
            { key: 'code', label: 'Code' },
            { key: 'phoneCode', label: 'Phone Code' },
            { key: 'phoneLength', label: 'Phone Length' },
            { key: 'currency', label: 'Currency' },
            { key: 'currencySymbol', label: 'Symbol' },
            {
              key: 'active',
              label: 'Active',
              render: (r) => (
                <div className="flex items-center gap-2 text-xs">
                  <Switch checked={!!r.active} onChange={(v) => toggleField(r, 'active', v)} />
                  {saving[r._id] && <span className="text-[11px] text-emerald-700">Saving…</span>}
                </div>
              ),
            },
            {
              key: 'actions',
              label: 'Actions',
              render: (r) => (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditFor(r)}
                    className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-slate-50"
                  >
                    <Edit3 className="h-3 w-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => removeRow(r)}
                    disabled={!!deleting[r._id]}
                    className="inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1 hover:bg-red-50 text-red-700 border-red-200 disabled:opacity-50"
                    title="Delete country"
                  >
                    {deleting[r._id] ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              )
            }
          ]}
          rows={rows}
        />
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Add Country"
        actions={[
          <button key="c" onClick={() => setOpen(false)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">Cancel</button>,
          <button key="s" onClick={add} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">Save</button>,
        ]}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Country Preset</label>
            <div className="mt-1 flex gap-2">
              <select
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value=""
                onChange={(e) => {
                  const p = COUNTRY_PRESETS.find(x => `${x.code}` === e.target.value);
                  if (p) applyCountryPreset(p, (vals) => setForm({ ...form, ...vals }));
                }}
              >
                <option value="">Select preset…</option>
                {COUNTRY_PRESETS.map(p => (
                  <option key={p.code} value={p.code}>{p.name} ({p.code}) {p.phoneCode}</option>
                ))}
              </select>
              <button
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                onClick={() => setForm({ name: '', code: '', phoneCode: '', phoneLength: '', currency: '', currencySymbol: '', language: 'en' })}
              >
                Clear
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600">Country Name *</label>
            <input className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-slate-600">Country Code *</label>
            <input className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g., US, UK, KW" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Phone Code *</label>
            <input className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" value={form.phoneCode} onChange={e => setForm({ ...form, phoneCode: e.target.value })} placeholder="e.g., +1, +965, +44" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Phone Number Length *</label>
            <input type="number" min="1" max="20" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" value={form.phoneLength} onChange={e => setForm({ ...form, phoneLength: e.target.value })} placeholder="e.g., 10" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Currency</label>
            <input className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} placeholder="e.g., USD, KWD" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Currency Symbol</label>
            <input className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" value={form.currencySymbol} onChange={e => setForm({ ...form, currencySymbol: e.target.value })} placeholder="e.g., $, KD" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Language</label>
            <select
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.language}
              onChange={(e) => setForm({ ...form, language: e.target.value === 'ar' ? 'ar' : 'en' })}
            >
              <option value="en">English</option>
              <option value="ar">Arabic</option>
            </select>
          </div>
        </div>
      </Modal>

      <Modal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        title="Edit Country"
        actions={[
          <button key="c" onClick={() => setOpenEdit(false)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            Cancel
          </button>,
          <button key="s" onClick={saveEdit} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">
            Save
          </button>,
        ]}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Country Preset</label>
            <div className="mt-1 flex gap-2">
              <select
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value=""
                onChange={(e) => {
                  const p = COUNTRY_PRESETS.find(x => `${x.code}` === e.target.value);
                  if (p) applyCountryPreset(p, (vals) => setEditForm({ ...editForm, ...vals }));
                }}
              >
                <option value="">Select preset…</option>
                {COUNTRY_PRESETS.map(p => (
                  <option key={p.code} value={p.code}>{p.name} ({p.code}) {p.phoneCode}</option>
                ))}
              </select>
              <button
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                onClick={() => setEditForm({ ...editForm, name: '', code: '', phoneCode: '', phoneLength: '', currency: '', currencySymbol: '', language: 'en' })}
              >
                Clear
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600">Country Name</label>
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Country Code</label>
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.code}
              onChange={(e) => setEditForm({ ...editForm, code: e.target.value.toUpperCase() })}
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Phone Code</label>
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.phoneCode}
              onChange={(e) => setEditForm({ ...editForm, phoneCode: e.target.value })}
              placeholder="e.g., +1, +965, +44"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Phone Number Length</label>
            <input
              type="number"
              min="1"
              max="20"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.phoneLength}
              onChange={(e) => setEditForm({ ...editForm, phoneLength: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Currency</label>
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.currency}
              onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Currency Symbol</label>
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={editForm.currencySymbol}
              onChange={(e) => setEditForm({ ...editForm, currencySymbol: e.target.value })}
            />
          </div>

          <div>
            <label className="text-xs text-slate-600">Language</label>
            <select
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={editForm.language}
              onChange={(e) => setEditForm({ ...editForm, language: e.target.value === 'ar' ? 'ar' : 'en' })}
            >
              <option value="en">English</option>
              <option value="ar">Arabic</option>
            </select>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!editForm.active}
              onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
            />
            Active
          </label>
        </div>
      </Modal>
    </div>
  );
}

/* ---------------- QR Poster ---------------- */
function QRPosterView({ token }) {
  const [posterForm, setPosterForm] = useState({ 
    english: { title: '', subtitle: '', caption: '', subcaption: '', tableLabelPrefix: '' },
    arabic: { title: '', subtitle: '', caption: '', subcaption: '', tableLabelPrefix: '' },
    fontSize: 16
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('english');

  useEffect(() => { 
    SettingsAPI.getPoster(token).then(setPosterForm).catch(console.error);
  }, [token]);

  const save = async () => {
    try {
      setSaving(true);
      const u = await SettingsAPI.setPoster(token, posterForm);
      setPosterForm(u); 
      alert('Poster settings saved');
    } finally { 
      setSaving(false); 
    }
  };

  const updateLanguageField = (lang, field, value) => {
    setPosterForm(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value
      }
    }));
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">QR Poster Content</h2>
        <button onClick={save} disabled={saving} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm text-white">
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
      
      {/* Language Tabs */}
      <div className="flex space-x-1 mb-6 bg-slate-100 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('english')}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'english' 
              ? 'bg-white text-slate-900 shadow-sm' 
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          English
        </button>
        <button
          onClick={() => setActiveTab('arabic')}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'arabic' 
              ? 'bg-white text-slate-900 shadow-sm' 
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          العربية
        </button>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700">Main Title</label>
          <input 
            className={`mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors ${
              activeTab === 'arabic' ? 'text-right' : ''
            }`}
            value={posterForm[activeTab]?.title || ''} 
            onChange={e => updateLanguageField(activeTab, 'title', e.target.value)} 
            placeholder={activeTab === 'english' ? "e.g., Prevent Food Waste" : "مثال: منع هدر الطعام"}
            dir={activeTab === 'arabic' ? 'rtl' : 'ltr'}
          />
        </div>
        
        <div>
          <label className="text-sm font-medium text-slate-700">Subtitle</label>
          <input 
            className={`mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors ${
              activeTab === 'arabic' ? 'text-right' : ''
            }`}
            value={posterForm[activeTab]?.subtitle || ''} 
            onChange={e => updateLanguageField(activeTab, 'subtitle', e.target.value)} 
            placeholder={activeTab === 'english' ? "e.g., Share Surplus" : "مثال: شارك الفائض"}
            dir={activeTab === 'arabic' ? 'rtl' : 'ltr'}
          />
        </div>
        
        <div>
          <label className="text-sm font-medium text-slate-700">Caption</label>
          <input 
            className={`mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors ${
              activeTab === 'arabic' ? 'text-right' : ''
            }`}
            value={posterForm[activeTab]?.caption || ''} 
            onChange={e => updateLanguageField(activeTab, 'caption', e.target.value)} 
            placeholder={activeTab === 'english' ? "e.g., Scan to Start Contributing" : "مثال: امسح للبدء في المساهمة"}
            dir={activeTab === 'arabic' ? 'rtl' : 'ltr'}
          />
        </div>
        
        <div>
          <label className="text-sm font-medium text-slate-700">Sub-caption</label>
          <input 
            className={`mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors ${
              activeTab === 'arabic' ? 'text-right' : ''
            }`}
            value={posterForm[activeTab]?.subcaption || ''} 
            onChange={e => updateLanguageField(activeTab, 'subcaption', e.target.value)} 
            placeholder={activeTab === 'english' ? "e.g., and Earn Rewards" : "مثال: وكسب المكافآت"}
            dir={activeTab === 'arabic' ? 'rtl' : 'ltr'}
          />
        </div>
        
        <div>
          <label className="text-sm font-medium text-slate-700">Table Label Prefix</label>
          <input 
            className={`mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors ${
              activeTab === 'arabic' ? 'text-right' : ''
            }`}
            value={posterForm[activeTab]?.tableLabelPrefix || ''} 
            onChange={e => updateLanguageField(activeTab, 'tableLabelPrefix', e.target.value)} 
            placeholder={activeTab === 'english' ? "e.g., Table:" : "مثال: طاولة:"}
            dir={activeTab === 'arabic' ? 'rtl' : 'ltr'}
          />
        </div>
        
        <div>
          <label className="text-sm font-medium text-slate-700">Font Size (px)</label>
          <div className="mt-1 flex items-center gap-3">
            <input 
              type="range"
              min="8"
              max="32"
              value={posterForm.fontSize || 16}
              onChange={e => setPosterForm(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
              className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer slider"
            />
            <span className="text-sm text-slate-600 min-w-[3rem] text-center">
              {posterForm.fontSize || 16}px
            </span>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-slate-50 rounded-xl">
          <h3 className="text-sm font-medium text-slate-700 mb-2">Preview ({activeTab === 'english' ? 'English' : 'العربية'})</h3>
          <div className={`text-slate-600 space-y-1 ${activeTab === 'arabic' ? 'text-right' : ''}`} dir={activeTab === 'arabic' ? 'rtl' : 'ltr'}>
            <div className="font-semibold" style={{ fontSize: `${(posterForm.fontSize || 16) * 1.5}px` }}>
              {posterForm[activeTab]?.title || (activeTab === 'english' ? 'Prevent Food Waste' : 'منع هدر الطعام')}
            </div>
            <div className="font-semibold" style={{ fontSize: `${(posterForm.fontSize || 16) * 1.5}px` }}>
              {posterForm[activeTab]?.subtitle || (activeTab === 'english' ? 'Share Surplus' : 'شارك الفائض')}
            </div>
            <div className="mt-2" style={{ fontSize: `${posterForm.fontSize || 16}px` }}>
              {posterForm[activeTab]?.caption || (activeTab === 'english' ? 'Scan to Start Contributing' : 'امسح للبدء في المساهمة')}
            </div>
            <div style={{ fontSize: `${posterForm.fontSize || 16}px` }}>
              {posterForm[activeTab]?.subcaption || (activeTab === 'english' ? 'and Earn Rewards' : 'وكسب المكافآت')}
            </div>
            <div className="text-slate-500" style={{ fontSize: `${(posterForm.fontSize || 16) * 0.75}px` }}>
              {posterForm[activeTab]?.tableLabelPrefix || (activeTab === 'english' ? 'Table:' : 'طاولة:')} Table 1
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Settings ---------------- */
function SettingsView({ token }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState('');
  const [pwOk, setPwOk] = useState('');

  useEffect(() => { 
    SettingsAPI.getBusiness(token).then(setForm).catch(console.error);
  }, [token]);

  const save = async () => {
    try {
      setSaving(true);
      const u = await SettingsAPI.setBusiness(token, form);
      setForm(u); alert('Saved');
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Business Profile</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setPwOpen(true)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">Change password</button>
            <button onClick={save} disabled={saving} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        <div className="space-y-4 mt-3">
          <div>
            <label className="text-xs text-slate-600">Business name</label>
            <input 
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors" 
              value={form.name} 
              onChange={e => setForm({ ...form, name: e.target.value })} 
              placeholder="Enter business name"
            />
          </div>
          
          <PhoneInput
            value={form.phone}
            onChange={(value) => setForm({ ...form, phone: value })}
            label="Phone number"
            required={true}
          />
          
          <div>
            <label className="text-xs text-slate-600">Email address</label>
            <input 
              type="email" 
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors" 
              value={form.email} 
              onChange={e => setForm({ ...form, email: e.target.value })} 
              placeholder="Enter email address"
            />
          </div>
          
          <AddressInput
            value={form.address}
            onChange={(value) => setForm({ ...form, address: value })}
            label="Complete address"
            required={true}
          />
        </div>
      </div>


      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="text-sm font-semibold">Security</div>
        <ul className="mt-2 text-sm text-slate-700 space-y-1">
          <li>• Admin-only access</li>
          <li>• JWT auth</li>
        </ul>
      </div>

      {/* Change Password Modal */}
      <Modal
        open={pwOpen}
        onClose={() => { setPwOpen(false); setPw({ current: '', next: '', confirm: '' }); setPwErr(''); setPwOk(''); }}
        title="Change Password"
        actions={[
          <button key="c" onClick={() => { setPwOpen(false); setPw({ current: '', next: '', confirm: '' }); setPwErr(''); setPwOk(''); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">Cancel</button>,
          <button key="s" onClick={async () => {
            try {
              setPwErr(''); setPwOk('');
              if (!pw.current || !pw.next || !pw.confirm) { setPwErr('Please fill all fields'); return; }
              if (pw.next.length < 8) { setPwErr('New password must be at least 8 characters'); return; }
              if (pw.next !== pw.confirm) { setPwErr('New password and confirm do not match'); return; }
              setPwBusy(true);
              await SettingsAPI.changePassword(token, pw.current, pw.next);
              setPwOk('Password updated successfully');
              setPw({ current: '', next: '', confirm: '' });
            } catch (e) {
              setPwErr(e?.message || 'Failed to change password');
            } finally { setPwBusy(false); }
          }} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm text-white" disabled={pwBusy}>{pwBusy ? 'Updating…' : 'Update'}</button>,
        ]}
      >
        <div className="space-y-3">
          {pwErr && <div className="text-sm text-rose-700">{pwErr}</div>}
          {pwOk && <div className="text-sm text-emerald-700">{pwOk}</div>}
          <div>
            <label className="text-xs text-slate-600">Current password</label>
            <input type="password" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-slate-600">New password</label>
            <input type="password" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-slate-600">Confirm new password</label>
            <input type="password" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ---------------- Payments ---------------- */
function PaymentsView({ token }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Payments</h2>
      
      {/* Settlement Accounts Section */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center">
            <BadgeDollarSign className="h-5 w-5 text-emerald-600" />
          </div>
          <h3 className="font-semibold text-slate-900">Settlement Accounts</h3>
        </div>

        {/* Payzah Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 hover:shadow-md transition-shadow">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h4 className="text-lg font-semibold text-slate-900">Payzah</h4>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 w-fit">
              Enabled
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- App ---------------- */
export default function App() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [tab, setTab] = useState('dashboard');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchData, setSearchData] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [donations, setDonations] = useState([]);
  const [merchants, setMerchants] = useState([]);

  useEffect(() => {
    // Initial fetch and authentication check
    (async () => {
      try {
        const [{ user: me }] = await Promise.all([AuthAPI.me(token)]);
        if (!me) {
          localStorage.clear();
          navigate('/login', { replace: true });
          return;
        }
        const [m, d] = await Promise.all([MerchantsAPI.list(token), DonorDonationsAPI.list(token)]);
        setMerchants(m); setDonations(d);
      } catch (e) {
        localStorage.clear();
        navigate('/login', { replace: true });
      }
    })();

    // Set up polling every 5 seconds to refresh donations and merchants
    const pollInterval = setInterval(async () => {
      try {
        const [m, d] = await Promise.all([MerchantsAPI.list(token), DonorDonationsAPI.list(token)]);
        setMerchants(m); setDonations(d);
      } catch (e) {
        console.error('Error polling data:', e);
      }
    }, 3000); // Poll every 5 seconds

    // Cleanup interval on unmount
    return () => clearInterval(pollInterval);
  }, []);

  // Debounced server-side search
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchData(null); return; }
    const t = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const data = await SearchAPI.query(token, q);
        setSearchData(data);
      } catch (e) {
        console.error(e);
        setSearchData({ merchants: [], receivers: [], donations: [] });
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, token]);

  const logout = async () => {
    try { await AuthAPI.logout(token); } catch { }
    finally { localStorage.clear(); location.href = '/login'; }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Topbar
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        onLogout={logout}
        user={user}
        query={searchQuery}
        onQuery={setSearchQuery}
      />

      <div className="mx-auto max-w-[1440px] px-3 md:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 py-4">
        <Sidebar current={tab} setCurrent={setTab} open={sidebarOpen} />

        <main className="min-h-[60vh]">
          {searchData && (
            <div className="mb-4">
              {searchLoading
                ? <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm">Searching…</div>
                : <SearchResults data={searchData} onClose={() => setSearchData(null)} />
              }
            </div>
          )}

          {tab === 'dashboard' && <Dashboard donations={donations} merchants={merchants} />}
          {tab === 'merchants' && <Merchants token={token} />}
          {tab === 'receivers' && <Receivers token={token} />}
          {tab === 'donations' && <Donations token={token} />}
          {tab === 'menu' && <MenuView token={token} />}
          {tab === 'qrcodes' && <QRCodes />}
          {tab === 'qposter' && <QRPosterView token={token} />}
          {tab === 'countries' && <Countries token={token} />}
          {tab === 'settings' && <SettingsView token={token} />}
          {tab === 'payments' && <PaymentsView token={token} />}
          {tab === 'map' && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <MapView markers={merchants.filter(x => x.lat && x.lng)} />
            </div>
          )}
        </main>
      </div>

      <footer className="py-8 text-center text-xs text-slate-500">
        HarethOptics Admin • Auth • {new Date().getFullYear()}
      </footer>
    </div>
  );
}
